import _ from "lodash";
import numeral from "numeral";

export const uuidv4 = () => {
    try {
        return crypto.randomUUID();
    } catch (e) {
        // Fallback: crypto.getRandomValues works in HTTP contexts unlike randomUUID()
        try {
            const bytes = new Uint8Array(16);
            crypto.getRandomValues(bytes);
            bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
            bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant bits
            const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
            return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
        } catch (e2) {
            // Last resort for very old runtimes without any crypto support
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = (Math.random() * 16) | 0;
                const v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        }
    }
};

/**
   * Accept data write to a file and save it in the filename supplied as parameter.
   * @param {string} fileName - The name of the file with format extension under which the data will be saved. e.g. testFile.csv/testfile.pdf/textfile.txt etc
   * @param {string} data - The data that needs to be written to the file before saving it.
   */
export const downloadFile = ({ fileName, data }) => {
    if (window.navigator.msSaveOrOpenBlob) { // EDGE workaround
        const blob = new Blob([decodeURIComponent(encodeURI(data))], { type: 'text/csv;charset=utf-8;' });
        navigator.msSaveBlob(blob, fileName);
    } else {
        const element = document.createElement('a');
        element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(data)}`);
        element.setAttribute('download', fileName);
        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    }
};

/**
   * Sanitize column data for CSV export.
   * @param {Array} tableData - Table data that has to be exported in CSV format.
   * @param {Array} columnNamesToBeSanitized  - Array of column names that have to be sanitized, empty array means all the columns will be sanitized.
   */
export const santizeTableDataForCsv = (tableData, columnNamesToBeSanitized = []) => {
    const startsWithSpecialChar = /^[=+\-@]/;
    return tableData.map(row => {
        const sanitizedRow = { ...row };
        for (const key in row) {
            if (row.hasOwnProperty(key) &&
                typeof row[key] === 'string' &&
                (columnNamesToBeSanitized.length === 0 || columnNamesToBeSanitized.includes(key)) &&
                startsWithSpecialChar.test(row[key])) {
                sanitizedRow[key] = _.join(["'", _.replace(row[key], startsWithSpecialChar, '')], '');
            }
        }
        return sanitizedRow;
    });
}

export const parentIs = (node, comparator) => node === comparator || (node.parentNode && parentIs(node.parentNode, comparator));

export const parentHasClass = (node, className) => (node.classList && node.classList.contains(className)) || (node.parentNode && parentHasClass(node.parentNode, className));

export const scrollToTop = () => {
    window.scroll(0, 0);
};

export const reflect = promise => promise.then(
    value => ({ value, status: "fulfilled" }),
    error => ({ error, status: "rejected" })
);

// This behaves like Promise.all, but will always execute all promises, even if one of them fails.
export const executeAllPromises = promises =>
    Promise.all(_.map(promises, reflect))
        .then(results => {
            if (_.every(results, { status: 'fulfilled' })) {
                return _.map(results, 'value');
            }
            const firstErrorMessage = _.find(results, { status: 'rejected' }).error;
            return Promise.reject(firstErrorMessage);
        });

export const normalizeCapacity = bytes => {
    const [capacity, unit] = numeral(bytes).format(`0.00 ib`).split(' ');
    return { value: capacity, capacity, unit };
};
